import c from 'classnames';
import { GenericLinkComponent, Icon, IconName, Typography } from '#components';
import styles from './Button.module.css';

import {
  ButtonSizes,
  ButtonVariants,
  ButtonColors,
  ButtonProps,
  ButtonContentProps,
  ButtonIconSize,
  LinkButtonProps,
} from './types';
import { resolveColor } from './utils/colors';
export type { ButtonProps, ButtonContentProps };
export { ButtonSizes, ButtonVariants, ButtonColors };

export const Button = (props: ButtonProps | LinkButtonProps) => {
  // Type guard to differentiate between LinkButton and standard Button
  if ('href' in props && props.href) {
    // Handling LinkButtonProps
    const {
      href,
      external,
      NavLinkComponent,
      // Common props for content and styling
      label,
      size = ButtonSizes.M,
      variant = ButtonVariants.SOLID,
      color = ButtonColors.GREEN,
      loading = false,
      disabled = false,
      width = 'content',
      className = '', // This className is for the link wrapper (<a> tag)
      iconNameLeft,
      iconNameRight,
      iconSize,
      children,
      onClick, // onClick for the button element within the link
      // ...restLink Specific props for the <a> tag itself, if any, are not explicitly handled by Button/withLink currently beyond standard <a> attributes
    } = props as LinkButtonProps; // Safe to cast due to type guard

    const { classKey, style: colorStyle } = resolveColor(color);
    const colorClass = styles[classKey];

    // Define classes for the inner button element (without the main className meant for the link wrapper)
    const innerButtonClasses = c(
      styles.button,
      styles.buttonFlex,
      styles[size],
      styles[variant],
      colorClass,
      width && styles[width],
      loading && styles.loading,
      disabled && !loading && styles.disabled,
      // Note: `className` from props is applied to the link wrapper by `withLink`
    );

    const buttonElementContent = (
      <button
        className={innerButtonClasses}
        style={colorStyle}
        disabled={loading || disabled}
        onClick={onClick}
        type='button'
      >
        {children ? (
          children
        ) : (
          <ButtonContent
            loading={loading}
            iconNameLeft={iconNameLeft}
            iconNameRight={iconNameRight}
            iconSize={iconSize}
            size={size}
          >
            {label ? (
              <Typography variant={size === ButtonSizes.S ? 'label' : 'body-s'}>
                {label}
              </Typography>
            ) : null}
          </ButtonContent>
        )}
      </button>
    );

    return withLink(buttonElementContent, href, {
      external: !!external,
      NavLinkComponent: NavLinkComponent,
      linkClassName: className,
    });
  } else {
    // Handling standard ButtonProps
    const {
      label,
      size = ButtonSizes.M,
      variant = ButtonVariants.SOLID,
      color = ButtonColors.GREEN,
      loading = false,
      disabled = false,
      width = 'content',
      className = '', // This className is for the button itself
      iconNameLeft,
      iconNameRight,
      iconSize,
      onClick,
      children,
      type,
      ...restButtonProps // Should only contain valid React.ButtonHTMLAttributes
    } = props as ButtonProps; // Safe to cast due to type guard

    const { classKey, style: colorStyle } = resolveColor(color);
    const colorClass = styles[classKey];

    const buttonClasses = c(
      styles.button,
      styles.buttonFlex,
      styles[size],
      styles[variant],
      colorClass,
      width && styles[width],
      loading && styles.loading,
      disabled && !loading && styles.disabled,
      className, // Apply className directly to the button
    );

    return (
      <button
        {...restButtonProps} // Spread valid button attributes
        className={buttonClasses}
        style={colorStyle}
        disabled={loading || disabled}
        type={type ?? 'button'}
        onClick={async () => {
          if (onClick) {
            await onClick();
          }
        }}
      >
        {children ? (
          children
        ) : (
          <ButtonContent
            loading={loading}
            iconNameLeft={iconNameLeft}
            iconNameRight={iconNameRight}
            iconSize={iconSize}
            size={size} // Pass size to ButtonContent
          >
            {label ? (
              <Typography variant={size === ButtonSizes.S ? 'label' : 'body-s'}>
                {label}
              </Typography>
            ) : null}
          </ButtonContent>
        )}
      </button>
    );
  }
};

type ButtonIconProps = { iconName: IconName; size: ButtonIconSize };

const ButtonIconLeft = ({ iconName, size }: ButtonIconProps) => (
  <Icon size={size} name={iconName} className={styles.iconLeft} />
);
const ButtonIconRight = ({ iconName, size }: ButtonIconProps) => (
  <Icon size={size} name={iconName} className={styles.iconRight} />
);

const ButtonContent = ({
  children,
  loading = false,
  iconNameRight,
  iconNameLeft,
  iconSize = 's',
  size = ButtonSizes.M,
}: ButtonContentProps) =>
  loading ? (
    <Typography variant={size === ButtonSizes.S ? 'label' : 'body-s'}>
      Loading...
    </Typography>
  ) : (
    <div className='flex align-items-center gap-1'>
      {iconNameLeft ? (
        <ButtonIconLeft iconName={iconNameLeft} size={iconSize} />
      ) : null}
      {children}
      {iconNameRight ? (
        <ButtonIconRight iconName={iconNameRight} size={iconSize} />
      ) : null}
    </div>
  );
export const withLink = (
  button: React.ReactNode,
  href: string | { pathname: string; search?: string },
  options: {
    external?: boolean;
    NavLinkComponent?: GenericLinkComponent;
    linkClassName?: string;
  },
) => {
  const url =
    typeof href === 'string' ? href : `${href.pathname}${href.search}`;
  return options.external ? (
    <a
      href={url}
      target='_blank'
      rel='noopener noreferrer'
      className={options.linkClassName}
    >
      {button}
    </a>
  ) : options.NavLinkComponent ? (
    <options.NavLinkComponent to={url} className={options.linkClassName}>
      {button}
    </options.NavLinkComponent>
  ) : (
    <a href={url} className={options.linkClassName}>
      {button}
    </a>
  );
};
export default Button;
